import React from "react";
import { motion } from "framer-motion";
import { Coins, Shield, Heart, TrendingUp, Layers, Waves, StickyNote } from "lucide-react";

/**
 * Phase 14.4 — reusable money/benefit flow diagram for service pages.
 *
 * Renders 3–4 nodes connected by gold arrows. Nodes fade in on scroll;
 * connectors animate a stroke dash between them. Respects
 * `prefers-reduced-motion`. Mobile: stacks vertically with a small down-arrow.
 *
 * Props:
 *   nodes:    [{ label, sub, icon }]  — 3–4 required
 *   caption:  short compliance-safe caption
 */

const ICON_MAP = {
  coin: Coins,
  shield: Shield,
  heart: Heart,
  trending: TrendingUp,
  layers: Layers,
  flow: Waves,
  note: StickyNote,
};

function Node({ node, index, total }) {
  const Icon = ICON_MAP[node.icon] || Shield;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.12, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid={`flow-node-${index}`}
      className="flex-1 min-w-[180px] max-w-[260px]"
    >
      <div className="relative rounded-2xl border border-lw-navy/10 bg-white p-5 sm:p-6 h-full shadow-[0_18px_40px_-32px_rgba(10,37,64,0.35)]">
        <span
          aria-hidden="true"
          className="absolute -top-3 -left-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-lw-navy text-white font-serif-display text-sm"
        >
          {index + 1}
        </span>
        <Icon size={28} className="text-lw-gold" strokeWidth={1.4} aria-hidden="true" />
        <h4 className="mt-4 font-serif-display text-lg text-lw-navy leading-snug">
          {node.label}
        </h4>
        {node.sub && (
          <p className="mt-1 text-xs text-lw-muted leading-relaxed">{node.sub}</p>
        )}
      </div>
    </motion.div>
  );
}

function Connector({ index }) {
  return (
    <div
      aria-hidden="true"
      className="hidden sm:flex items-center justify-center flex-shrink-0"
      style={{ width: 48 }}
    >
      <motion.svg
        viewBox="0 0 48 24"
        width="48"
        height="24"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6, delay: 0.15 + index * 0.12 }}
      >
        <motion.path
          d="M2 12 L42 12"
          stroke="#D4A24C"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="4 4"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8, delay: 0.1 + index * 0.12 }}
        />
        <path d="M38 6 L46 12 L38 18" fill="none" stroke="#D4A24C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </motion.svg>
    </div>
  );
}

function MobileArrow() {
  return (
    <div className="sm:hidden flex justify-center my-2" aria-hidden="true">
      <svg width="16" height="24" viewBox="0 0 16 24">
        <path d="M8 2 L8 20" stroke="#D4A24C" strokeWidth="2" strokeDasharray="3 3" />
        <path d="M3 16 L8 22 L13 16" fill="none" stroke="#D4A24C" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

export const ServiceFlowDiagram = ({ nodes = [], caption }) => {
  if (!nodes.length) return null;
  return (
    <div data-testid="service-flow-diagram" className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-stretch gap-4 sm:gap-0">
        {nodes.map((n, i) => (
          <React.Fragment key={i}>
            <Node node={n} index={i} total={nodes.length} />
            {i < nodes.length - 1 && (
              <>
                <Connector index={i} />
                <MobileArrow />
              </>
            )}
          </React.Fragment>
        ))}
      </div>
      {caption && (
        <p className="mt-6 text-sm text-lw-muted leading-relaxed italic">{caption}</p>
      )}
    </div>
  );
};

export default ServiceFlowDiagram;
